import React, { useEffect, useRef, useCallback } from 'react';

const formatTime = (time) => {
  if (time && !isNaN(time)) {
    const minutes = Math.floor(time / 60);
    const formatMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(time % 60);
    const formatSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${formatMinutes}:${formatSeconds}`;
  }
  return '00:00';
};

const ProgressBar = React.memo(({ audioRef, progressBarRef, duration, setTimeProgress }) => {
  const playAnimationRef = useRef();
  const isDragging = useRef(false);
  const lastParentUpdate = useRef(0);
  const currentTimeRef = useRef(null);

  const updateParent = useCallback((currentTime, force = false) => {
    const now = Date.now();
    if (force || now - lastParentUpdate.current > 500) {
      setTimeProgress(currentTime);
      lastParentUpdate.current = now;
    }
  }, [setTimeProgress]);

  // RAF-Loop: Timer-DOM direkt schreiben — kein React State, kein Re-render
  const repeat = useCallback(() => {
    if (audioRef.current && !isDragging.current) {
      const currentTime = audioRef.current.currentTime;

      if (currentTimeRef.current) {
        currentTimeRef.current.textContent = formatTime(currentTime);
      }

      if (progressBarRef.current) {
        progressBarRef.current.value = currentTime;
        progressBarRef.current.max = duration;
        progressBarRef.current.style.setProperty(
          '--range-progress',
          `${(currentTime / duration) * 100}%`
        );
      }

      updateParent(currentTime);
    }
    playAnimationRef.current = requestAnimationFrame(repeat);
  }, [audioRef, duration, progressBarRef, updateParent]);

  useEffect(() => {
    playAnimationRef.current = requestAnimationFrame(repeat);
    return () => {
      cancelAnimationFrame(playAnimationRef.current);
    };
  }, [repeat]);

  const handleProgressStart = useCallback(() => {
    isDragging.current = true;
    cancelAnimationFrame(playAnimationRef.current);
  }, []);

  const handleProgressChange = useCallback(() => {
    if (progressBarRef.current) {
      const newTime = parseFloat(progressBarRef.current.value);

      if (currentTimeRef.current) {
        currentTimeRef.current.textContent = formatTime(newTime);
      }

      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
      }

      progressBarRef.current.style.setProperty(
        '--range-progress',
        `${(newTime / duration) * 100}%`
      );
    }
  }, [audioRef, duration, progressBarRef]);

  const handleProgressEnd = useCallback(() => {
    isDragging.current = false;

    if (progressBarRef.current && audioRef.current) {
      const newTime = parseFloat(progressBarRef.current.value);
      updateParent(newTime, true);
      playAnimationRef.current = requestAnimationFrame(repeat);
    }
  }, [updateParent, repeat, progressBarRef, audioRef]);

  return (
    duration > 0 && (
      <div className="progress">
        <span className="time" ref={currentTimeRef}>00:00</span>
        <input
          className="progress-bar"
          type="range"
          ref={progressBarRef}
          defaultValue="0"
          onMouseDown={handleProgressStart}
          onTouchStart={handleProgressStart}
          onChange={handleProgressChange}
          onMouseUp={handleProgressEnd}
          onTouchEnd={handleProgressEnd}
        />
        <span className="time">{formatTime(duration)}</span>
      </div>
    )
  );
});

export default ProgressBar;
